import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  type AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  type ValidationErrors,
  type ValidatorFn,
  Validators,
} from '@angular/forms';
import { ADMIN_DOMAIN_AMPLIFIEDIT } from '@cdw-ae/common-data';
import { MatDialogRef } from '@cdw-ae/common-frontend-materials';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import { CdwDialogModule } from '@cdw-ae/common-ui-components-dialog';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import {
  addAdmin,
  addAdminFailure,
  addAdminSuccess,
} from '../../../../../+state/platform-admins/platform-admins.actions';
import { environment } from '../../../../../../environments/environment';

/**
 * Reactive validator: the email's domain must be in this environment's
 * client-side allowlist. The server remains authoritative; this is instant UX.
 */
function allowedDomainValidator(domains: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '')
      .trim()
      .toLowerCase();
    const at = value.lastIndexOf('@');
    if (!value || at < 0) {
      return null; // required / email validators own these cases
    }
    return domains.includes(value.slice(at + 1))
      ? null
      : { disallowedDomain: true };
  };
}

@Component({
  selector: 'cdw-add-platform-admin',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CdwDialogModule,
    CdwIconModule,
    CdwCheckboxModule,
    CommonUiComponentsTextInputModule,
  ],
  templateUrl: './add-platform-admin.component.html',
  styleUrl: './add-platform-admin.component.scss',
})
export class AddPlatformAdminComponent {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<AddPlatformAdminComponent>);

  readonly allowedDomains = environment.allowedAdminEmailDomains;
  readonly emailPlaceholder = `name@${ADMIN_DOMAIN_AMPLIFIEDIT}`;

  readonly form = this.fb.group({
    email: [
      '',
      [
        Validators.required,
        Validators.email,
        allowedDomainValidator(this.allowedDomains),
      ],
    ],
    name: ['', [Validators.required]],
    isSuper: [false],
  });

  submitting = false;
  serverError: string | null = null;

  get emailControl() {
    return this.form.controls.email;
  }
  get nameControl() {
    return this.form.controls.name;
  }

  submit(): void {
    if (this.submitting) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { email, name, isSuper } = this.form.getRawValue();
    this.submitting = true;
    this.serverError = null;
    this.store.dispatch(
      addAdmin({ email: email ?? '', name: name ?? '', isSuper: isSuper ?? false })
    );

    // Close on success; surface the server message (domain 400 / 409) inline.
    this.actions$
      .pipe(ofType(addAdminSuccess, addAdminFailure), take(1))
      .subscribe((action) => {
        this.submitting = false;
        if (action.type === addAdminSuccess.type) {
          this.dialogRef.close(true);
        } else {
          this.serverError = (
            action as ReturnType<typeof addAdminFailure>
          ).error;
        }
      });
  }

  cancel(): void {
    if (this.submitting) {
      return;
    }
    this.dialogRef.close(false);
  }
}
